import React, { useState } from "react";

import { WORKSPACE_STATE } from "../../context/ActiveBusinessContext";
import { logoutSession } from "../../utils/auth";
import "../../routes/AppRoutes.css";
import "../../styles/pro-dashboard.css";

// Rendered by ProfessionalRouteGuard in place of a workspace page whenever the
// workspace has not resolved. Every branch carries an action: the bug this
// replaces showed "Select a business workspace to manage settings" on a page
// with no workspace to select, no retry, and no way out.
//
// Styling reuses .workspace-transition from routes/AppRoutes.css, which already
// exists for the stalled-hydration escape hatch and solves the same problem.
const COPY = {
  [WORKSPACE_STATE.UNAVAILABLE]: {
    title: "We couldn't load your workspace",
    body:
      "This is usually a connection problem, not a problem with your account. Your workspace and its data are unaffected.",
    action: "Try again",
  },
  [WORKSPACE_STATE.EMPTY]: {
    title: "Your workspace isn't ready yet",
    body:
      "Your account exists, but we couldn't find a workspace attached to it. Setting it up again usually fixes this straight away.",
    action: "Set up my workspace",
  },
};

const WorkspaceUnavailable = ({ state, onRetry }) => {
  const [retrying, setRetrying] = useState(false);
  const [retryFailed, setRetryFailed] = useState(false);
  const copy = COPY[state] || COPY[WORKSPACE_STATE.UNAVAILABLE];

  const handleRetry = async () => {
    if (retrying) return;
    setRetrying(true);
    setRetryFailed(false);
    try {
      // refresh() re-runs the /api/auth/me bootstrap. On the server side that
      // call also re-runs the workspace self-heal, so this doubles as the
      // repair path for an account that genuinely resolved to nothing --
      // which is why there is no separate "create workspace" endpoint.
      const result = await onRetry?.();
      if (!result) setRetryFailed(true);
    } catch (_error) {
      setRetryFailed(true);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <section className="workspace-transition" role="alert">
      <h1>{copy.title}</h1>
      <p>{copy.body}</p>
      {retryFailed && (
        <p>That didn&apos;t work. If it keeps happening, contact support and we&apos;ll sort it out.</p>
      )}
      <div className="workspace-transition__actions">
        <button
          type="button"
          className="pro-btn pro-btn--primary"
          onClick={handleRetry}
          disabled={retrying}
        >
          {retrying ? "Checking…" : copy.action}
        </button>
        <a className="pro-btn" href="/pro/settings/account">Account settings</a>
        <button type="button" className="pro-btn" onClick={() => logoutSession()}>
          Sign out
        </button>
        <a className="pro-btn pro-btn--ghost" href="/contact">Contact support</a>
      </div>
    </section>
  );
};

export default WorkspaceUnavailable;
